import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import TrackerTable from './components/TrackerTable/TrackerTable';
import Spinner from 'react-bootstrap/Spinner';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getCurrMonth } from './shared/functions/dateProcessor';
import {
  filterStorageTypeMap,
  TABLE_REFETCH_INTERVAL_MILISEC,
  TrackerDataType,
  visibleIssueCols,
} from './shared/constants/constants';
import { useMediaInteractionHandler } from './shared/eventHandler/MediaInteractionEventHandler';
import { useLocation, useParams } from 'react-router-dom';
import { useServiceLogHandler } from './shared/eventHandler/ServiceLogEventHandler';
import { useFilters, useFiltersDispatch } from './components/TrackerFilter/Context/FiltersContext';
import isFilterEmpty from './shared/functions/isFilterEmpty';
import dayjs from '../src/utils/dayjs';
import MonthSelector from './components/TrackerTable/MonthSelector';
import { Box } from '@mui/material';
import TrackerTableFuzzySearch from './components/TrackerTable/TrackerTableFuzzySearch';
import TrackerTableFilter from './components/TrackerTable/TrackerTableFilter';
import { TrackerTableActionButtons } from './components/TrackerTable/TrackerTableActionButtons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFilteredInteractions,
  getFuzzySearchInteractions,
  getMonthlyInteractions,
} from './shared/requests/interactionsRequests';
import { useRecentCreations } from './shared/hooks/useRecentCreations';
import { getTrackerFilterQueryFromFilters } from './components/TrackerFilter/trackerFilterHelpers';

const tableContainerSx = {
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  paddingLeft: { xs: '12px', sm: '20px', md: '28px' },
  paddingRight: { xs: '12px', sm: '20px', md: '28px' },
  paddingTop: { xs: '10px', sm: '14px', md: '18px' },
  paddingBottom: { xs: '10px', sm: '12px', md: '14px' },
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const filterContainerSx = {
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '1rem',
  rowGap: '0.75rem',
  marginTop: '1rem',
  marginBottom: '1rem',
  minHeight: '32px',
};

const filterWrapperSx = {
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '1rem',
  rowGap: '0.75rem',
  alignItems: 'center',
};

const actionButtonsWrapperSx = {
  display: 'flex',
  flexWrap: 'wrap',
  flexShrink: 0,
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  gap: '1rem',
  rowGap: '0.75rem',
};

const tableRegionSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

function App({ dropdownTable, user }) {
  const [data, setData] = useState([]);
  const filtersDispatch = useFiltersDispatch();
  const { state } = useLocation();
  const recentCreations = useRecentCreations();
  const { markCreated } = recentCreations;
  const previousMonthlyIds = useRef(null);

  let month;
  if (sessionStorage.getItem('currMonth')) {
    month = sessionStorage.getItem('currMonth');
  } else {
    month = getCurrMonth();
  }

  const [currMonth, setMonth] = useState(month);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedBulkEditRowIds, setSelectedBulkEditRowIds] = useState([]);

  const existingFilters = useFilters();
  const storedFilter =
    user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.MEDIA_INTERACTION]];
  const hasActiveFuzzySearch = Boolean(state?.queryString && state?.category);

  const queryClient = useQueryClient();
  const onSuccessCallback = async () => {
    await queryClient.invalidateQueries({ queryKey: ['filteredIssues'] });
    await queryClient.invalidateQueries({ queryKey: ['monthlyIssues'] });
    await queryClient.invalidateQueries({ queryKey: ['fuzzySearch', TrackerDataType.MEDIA_INTERACTION] });
  };
  const serviceLogEventHandler = useServiceLogHandler({
    recentCreations,
    onSuccessCallback,
  });
  const mediaInteractionEventHandler = useMediaInteractionHandler({
    recentCreations,
    onSuccessCallback,
  });

  const issueParams = useParams();

  const { data: monthlyIssues, refetch: refetchMontlyIssues, isLoading: isMonthlyIssuesLoading } = useQuery({
    queryKey: ['monthlyIssues', currMonth],
    queryFn: () => getMonthlyInteractions(currMonth, dayjs.tz.guess()),
    enabled: !hasActiveFuzzySearch && isFilterEmpty(existingFilters) && !storedFilter,
    refetchInterval: TABLE_REFETCH_INTERVAL_MILISEC,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const filtersQueryString = useMemo(
    () => getTrackerFilterQueryFromFilters(existingFilters, TrackerDataType.MEDIA_INTERACTION),
    [existingFilters]
  );

  const { data: filteredIssues } = useQuery({
    queryKey: ['filteredIssues', filtersQueryString],
    queryFn: async () => getFilteredInteractions(filtersQueryString),
    enabled: !!filtersQueryString,
  });

  const { data: fuzzyIssues } = useQuery({
    queryKey: ['fuzzySearch', TrackerDataType.MEDIA_INTERACTION, state?.queryString, state?.category],
    queryFn: () => getFuzzySearchInteractions(state?.queryString, state?.category),
    enabled: hasActiveFuzzySearch,
  });

  const shouldDisableMonthSwitches = () => {
    const hasFilters = Object.keys(existingFilters).length > 0;
    const isQuickSearching = hasActiveFuzzySearch;
    return hasFilters || isQuickSearching;
  };

  const detailedExportContext = useMemo(() => {
    if (hasActiveFuzzySearch) {
      return {
        trackerType: TrackerDataType.MEDIA_INTERACTION,
        mode: 'fuzzy',
        queryString: state?.queryString,
        category: state?.category,
      };
    }

    if (!isFilterEmpty(existingFilters) && filtersQueryString) {
      return {
        trackerType: TrackerDataType.MEDIA_INTERACTION,
        mode: 'filter',
        filterQueryString: filtersQueryString,
      };
    }

    return {
      trackerType: TrackerDataType.MEDIA_INTERACTION,
      mode: 'monthly',
      month: currMonth,
      timezoneOffset: dayjs.tz.guess(),
    };
  }, [currMonth, existingFilters, filtersQueryString, hasActiveFuzzySearch, state?.category, state?.queryString]);

  const handleBulkEditSelectAll = (isChecked, pageRowIds) => {
    if (isChecked) {
      setSelectedBulkEditRowIds(pageRowIds);
    } else {
      setSelectedBulkEditRowIds([]);
    }
  };

  const handleBulkEditSelectRow = (isChecked, rowId) => {
    setSelectedBulkEditRowIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, rowId];
      } else {
        return prevSelectedIds.filter((id) => rowId !== id);
      }
    });
  };

  // Filter service based on url
  useEffect(() => {
    if (issueParams.issueId) {
      filtersDispatch({
        type: 'setFilters',
        payload: {
          id: issueParams.issueId,
        },
      });
    }
  }, [issueParams.issueId, filtersDispatch]);

  useEffect(() => {
    if (!monthlyIssues && !filteredIssues && !fuzzyIssues) {
      return;
    }
    if (hasActiveFuzzySearch && fuzzyIssues) {
      setData(fuzzyIssues);
      return;
    }
    if (!isFilterEmpty(existingFilters) && filteredIssues) {
      setData(filteredIssues);
      return;
    }
    if (isFilterEmpty(existingFilters) && !hasActiveFuzzySearch && !storedFilter && monthlyIssues) {
      setData(monthlyIssues);
    }
  }, [monthlyIssues, filteredIssues, fuzzyIssues, existingFilters, hasActiveFuzzySearch, storedFilter]);

  useEffect(() => {
    previousMonthlyIds.current = null;
  }, [currMonth]);

  useEffect(() => {
    if (!Array.isArray(monthlyIssues)) {
      return;
    }
    const shouldTrack = isFilterEmpty(existingFilters) && !hasActiveFuzzySearch && !storedFilter;
    const nextIds = new Set(monthlyIssues.map((row) => row.id));

    if (shouldTrack && previousMonthlyIds.current) {
      const prevIds = previousMonthlyIds.current;
      monthlyIssues.forEach((row) => {
        if (!prevIds.has(row.id)) {
          markCreated(row.id);
        }
      });
    }

    previousMonthlyIds.current = nextIds;
  }, [monthlyIssues, existingFilters, hasActiveFuzzySearch, storedFilter, markCreated]);

  useEffect(() => {
    sessionStorage.setItem('currMonth', currMonth);
    if (!hasActiveFuzzySearch && isFilterEmpty(existingFilters)) {
      refetchMontlyIssues();
    }
  }, [currMonth, existingFilters, hasActiveFuzzySearch, refetchMontlyIssues]);

  const safeRows = Array.isArray(data) ? data : [];
  const monthSwitchesDisabled = shouldDisableMonthSwitches();
  const isMonthTransitionLoading = !monthSwitchesDisabled && isMonthlyIssuesLoading;

  return (
    <Box sx={tableContainerSx}>
      <Box>
        <MonthSelector
          disabled={monthSwitchesDisabled}
          currMonth={currMonth}
          onMonthChange={setMonth}
          showTodayButton={false}
          showContainerCard={false}
          labelSx={{ fontWeight: 400 }}
        />
      </Box>
      <Box sx={filterContainerSx}>
        <Box sx={filterWrapperSx}>
          <TrackerTableFuzzySearch />
          <TrackerTableFilter
            setDataCallback={setData}
            eventHandler={mediaInteractionEventHandler}
            dropdownTable={dropdownTable}
          />
        </Box>
        <Box sx={actionButtonsWrapperSx}>
          <TrackerTableActionButtons
            eventHandler={mediaInteractionEventHandler}
            dropdownTable={dropdownTable}
            currMonth={currMonth}
            rows={safeRows}
            isBulkEdit={isBulkEdit}
            detailedExportContext={detailedExportContext}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            selectedBulkEditRowsCount={selectedBulkEditRowIds.length}
            onSwitchToBulkEditView={() => {
              setIsBulkEdit(true);
            }}
            onSwitchToMonthlyView={() => {
              setIsBulkEdit(false);
              setSelectedBulkEditRowIds([]);
            }}
          />
        </Box>
      </Box>
      {Array.isArray(data) && dropdownTable ? (
        <Box sx={{ ...tableRegionSx, position: 'relative' }} aria-busy={isMonthTransitionLoading}>
          <TrackerTable
            rows={safeRows}
            dropdownTable={dropdownTable}
            visibleCols={visibleIssueCols}
            eventHandler={mediaInteractionEventHandler}
            linkedEventHandler={serviceLogEventHandler}
            user={user}
            isBulkEdit={isBulkEdit}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            onBulkEditSelectAll={handleBulkEditSelectAll}
            onBulkEditSelectRow={handleBulkEditSelectRow}
            fitToContainer
          />
          {isMonthTransitionLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                backgroundColor: 'rgba(251, 252, 254, 0.56)',
              }}
            >
              <div className="spinner-container">
                <Spinner animation="border" />
              </div>
            </Box>
          ) : null}
        </Box>
      ) : (
        <div className="spinner-container">
          <Spinner animation="border" />
        </div>
      )}
    </Box>
  );
}

export default App;
